/**
 * The phone's magnetometer, as a true-north heading.
 *
 * GPS gives a course over ground, which is only meaningful while actually
 * moving — stand still on a tee and turn on the spot and it reports nothing at
 * all. That is why the map used to sit frozen whichever way the golfer faced.
 * The compass answers the question GPS cannot: which way is the phone pointing
 * right now, at a standstill.
 */

/** iOS exposes a true-north heading Safari's spec-compliant path does not. */
interface IosOrientationEvent extends DeviceOrientationEvent {
  webkitCompassHeading?: number;
  webkitCompassAccuracy?: number;
}

interface PermissionCapableCtor {
  requestPermission?: () => Promise<'granted' | 'denied' | 'default'>;
}

function ctor(): (typeof DeviceOrientationEvent & PermissionCapableCtor) | null {
  return typeof window !== 'undefined' && 'DeviceOrientationEvent' in window
    ? (window.DeviceOrientationEvent as typeof DeviceOrientationEvent & PermissionCapableCtor)
    : null;
}

export const compassSupported = (): boolean => ctor() !== null;

/**
 * Ask for motion access.
 *
 * iOS 13+ gates the orientation sensors behind a permission that can ONLY be
 * requested from inside a user gesture — call it on load and it rejects
 * silently, which is the sort of failure that looks like a broken feature. The
 * caller must invoke this from a real tap.
 */
export async function requestCompass(): Promise<boolean> {
  const C = ctor();
  if (!C) return false;
  if (typeof C.requestPermission !== 'function') return true; // Android, desktop
  try {
    return (await C.requestPermission()) === 'granted';
  } catch {
    return false;
  }
}

/**
 * Smooth a heading across the 360/0 seam.
 *
 * Averaging degrees naively sends the map spinning the long way round every
 * time the golfer faces north — 359 to 1 reads as a 358 degree turn. Taking the
 * shorter signed difference keeps it to two.
 */
export function smoothHeading(prev: number | null, next: number, alpha = 0.2): number {
  if (prev == null) return next;
  const delta = ((next - prev + 540) % 360) - 180;
  return (prev + delta * alpha + 360) % 360;
}

/** Starts listening. Returns the unsubscribe. */
export function watchCompass(onHeading: (deg: number) => void): () => void {
  const handler = (e: DeviceOrientationEvent) => {
    const ios = e as IosOrientationEvent;
    let deg: number | null = null;

    if (typeof ios.webkitCompassHeading === 'number' && !Number.isNaN(ios.webkitCompassHeading)) {
      // Already degrees clockwise from TRUE north — the value we want.
      deg = ios.webkitCompassHeading;
    } else if (e.absolute && typeof e.alpha === 'number') {
      // The spec's alpha runs anticlockwise from north, so it has to be flipped.
      deg = 360 - e.alpha;
    }

    if (deg == null || Number.isNaN(deg)) return;
    onHeading(((deg % 360) + 360) % 360);
  };

  window.addEventListener('deviceorientation', handler, true);
  return () => window.removeEventListener('deviceorientation', handler, true);
}
